import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Newspaper, PenTool, Mic, Radio, Globe, Search, ArrowRight, Rss } from 'lucide-react';

const JournalismMedia: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "The Press Institute";
    
    return (
        <div className="min-h-screen bg-[#fcfbf9] font-serif text-[#111] selection:bg-[#cc0000] selection:text-white border-t-[10px] border-[#111]">
            {/* Top Date Bar */}
            <div className="border-b-2 border-[#111] py-3 px-8 flex justify-between items-center font-sans text-xs font-bold uppercase tracking-widest">
                <span>{new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
                <div className="flex gap-6">
                    <a href="#" className="hover:text-[#cc0000]">Subscribe</a>
                    <a href="#" className="hover:text-[#cc0000]">Log In</a>
                </div>
            </div>

            {/* Header / Masthead */}
            <header className="py-12 px-8 text-center border-b border-[#ddd]">
                <h1 className="text-6xl md:text-8xl font-black uppercase tracking-tighter leading-none mb-4">{schoolName}</h1>
                <div className="flex items-center justify-center gap-4 text-xs font-sans font-bold uppercase tracking-[0.2em] text-[#666]">
                    <span>Truth</span>
                    <div className="w-1 h-1 bg-[#cc0000] rounded-full"></div>
                    <span>Integrity</span>
                    <div className="w-1 h-1 bg-[#cc0000] rounded-full"></div>
                    <span>Impact</span>
                </div>
            </header>

            {/* Navbar */}
            <nav className="sticky top-0 z-50 bg-white border-b-2 border-[#111] py-4 shadow-sm">
                <div className="max-w-7xl mx-auto px-8 flex justify-center md:justify-between items-center">
                    <div className="hidden md:flex gap-8 font-sans text-xs font-bold uppercase tracking-widest">
                        <a href="#" className="hover:text-[#cc0000] transition-colors">Investigative</a>
                        <a href="#" className="hover:text-[#cc0000] transition-colors">Broadcast</a>
                        <a href="#" className="hover:text-[#cc0000] transition-colors">Digital</a>
                        <a href="#" className="hover:text-[#cc0000] transition-colors">Photojournalism</a>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="relative">
                             <Search size={16} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
                             <input type="text" placeholder="SEARCH ARCHIVES" className="pl-8 pr-4 py-1 border border-[#ccc] bg-transparent text-[10px] font-sans font-bold w-48 focus:outline-none focus:border-[#111]" />
                        </div>
                    </div>
                </div>
            </nav>

            {/* Main Content Grid */}
            <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 border-x border-[#ddd] min-h-screen">
                
                {/* Left Column (Main Story) */}
                <main className="lg:col-span-8 p-8 lg:p-12 border-b lg:border-b-0 lg:border-r border-[#ddd]">
                    <span className="font-sans text-[10px] font-bold uppercase tracking-widest text-[#cc0000] mb-4 block">Breaking News • Admissions Open</span>
                    
                    <h2 className="text-5xl md:text-6xl font-bold leading-[0.95] mb-6">
                        Cultivating the Next Generation of Watchdogs.
                    </h2>
                    
                    <div className="h-[400px] w-full bg-gray-200 mb-8 overflow-hidden grayscale contrast-125 relative">
                        <img src="https://images.unsplash.com/photo-1504711434969-e33886168f5c?q=80&w=2070&auto=format&fit=crop" className="w-full h-full object-cover" alt="Press Room" />
                        <div className="absolute bottom-0 left-0 bg-[#cc0000] text-white px-3 py-1 text-[10px] font-sans font-bold uppercase tracking-widest">
                            Live from the Newsroom
                        </div>
                    </div>
                    
                    <div className="md:columns-2 gap-8 text-lg leading-relaxed font-serif text-[#333]">
                        <p className="mb-6 first-letter:text-5xl first-letter:font-bold first-letter:mr-2 first-letter:float-left">
                            In an era of misinformation, the world needs rigorous reporting more than ever. Our curriculum combines traditional ethics with cutting-edge digital forensics. Students learn by doing, publishing real stories for our award-winning wire service.
                        </p>
                        <p className="mb-6">
                            From data journalism to podcasting, we prepare you for the evolving media landscape. Join a faculty of Pulitzer Prize winners and industry veterans dedicated to the pursuit of truth.
                        </p>
                    </div>

                    <div className="mt-8 border-t border-[#111] pt-8">
                         <Button className="rounded-none bg-[#111] hover:bg-[#cc0000] text-white font-sans font-bold uppercase tracking-widest text-xs h-12 px-8">
                             Read Full Prospectus <ArrowRight size={14} className="ml-2" />
                         </Button>
                    </div>
                </main>

                {/* Right Column (Sidebar) */}
                <aside className="lg:col-span-4 bg-[#f4f4f4]">
                    <div className="p-8 border-b border-[#ddd]">
                        <h3 className="font-sans text-sm font-bold uppercase tracking-widest mb-6 border-b-2 border-[#111] pb-2">Latest Curriculum</h3>
                        <ul className="space-y-6">
                            {[
                                { title: "Data Visualization", desc: "Telling stories with code." },
                                { title: "Audio Storytelling", desc: "The art of the podcast." },
                                { title: "Conflict Reporting", desc: "Safety in hostile environments." },
                                { title: "Media Law", desc: "Understanding the First Amendment." }
                            ].map((item, i) => (
                                <li key={i} className="group cursor-pointer">
                                    <h4 className="font-bold text-lg leading-tight mb-1 group-hover:text-[#cc0000] transition-colors">{item.title}</h4>
                                    <p className="font-sans text-xs text-[#666]">{item.desc}</p>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="p-8">
                        <h3 className="font-sans text-sm font-bold uppercase tracking-widest mb-6 border-b-2 border-[#111] pb-2">Opinion</h3>
                        <div className="italic text-xl font-medium leading-normal mb-4">
                            "The press was protected so that it could bare the secrets of government and inform the people."
                        </div>
                        <div className="font-sans text-xs font-bold uppercase text-[#666]">— Black, J.</div>
                    </div>
                </aside>
            </div>

             {/* Footer */}
            <footer className="bg-[#111] text-white py-16 px-8 border-t-8 border-[#cc0000]">
                 <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between gap-12">
                     <div>
                         <Newspaper size={32} className="mb-6" />
                         <span className="font-bold text-2xl uppercase tracking-widest block mb-2">{schoolName}</span>
                         <span className="font-sans text-[10px] uppercase tracking-widest text-gray-500">Est. 1908</span>
                     </div>
                     
                     <div className="grid grid-cols-2 md:grid-cols-4 gap-12 font-sans text-[10px] font-bold uppercase tracking-widest text-gray-400">
                         <ul className="space-y-2">
                             <li><a href="#" className="hover:text-white">About Us</a></li>
                             <li><a href="#" className="hover:text-white">Leadership</a></li>
                             <li><a href="#" className="hover:text-white">History</a></li>
                         </ul>
                         <ul className="space-y-2">
                             <li><a href="#" className="hover:text-white">Admissions</a></li>
                             <li><a href="#" className="hover:text-white">Tuition</a></li>
                             <li><a href="#" className="hover:text-white">Financial Aid</a></li>
                         </ul>
                         <ul className="space-y-2">
                             <li><a href="#" className="hover:text-white">Alumni</a></li>
                             <li><a href="#" className="hover:text-white">Events</a></li>
                             <li><a href="#" className="hover:text-white">Awards</a></li>
                         </ul>
                         <ul className="space-y-2">
                             <li><a href="#" className="hover:text-white">Contact</a></li>
                             <li><a href="#" className="hover:text-white">Careers</a></li>
                             <li><a href="#" className="hover:text-white">Ethics Policy</a></li>
                         </ul>
                     </div>
                 </div>
                 <div className="max-w-7xl mx-auto mt-12 pt-4 border-t border-gray-800 text-center font-sans text-[10px] text-gray-600 uppercase tracking-widest">
                     © 2026 {schoolName}. All rights reserved.
                 </div>
            </footer>
        </div>
    );
};

export default JournalismMedia;
